import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  StyledComponentProps,
  SxProps,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorsHandler } from '~/components/ErrorsHandler';
import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';
import { mergeSxProps } from '~/utils/helper';

import { ClassKey, useSxStyles } from './useStyles';

interface CustomSxStyles {
  root: SxProps<Theme>;
  header: SxProps<Theme>;
  content: SxProps<Theme>;
  actions: SxProps<Theme>;
}

// TODO: should delete 'extends StyledComponentProps<ClassKey>' from Widget component after refactoring
export interface WidgetProps extends StyledComponentProps<ClassKey> {
  title: React.ReactNode;
  content: JSX.Element;
  actions?: JSX.Element;
  dataCy?: string;
  customSxStyles?: Partial<CustomSxStyles>;
}

export const Widget: React.FC<WidgetProps> = (props) => {
  const { title, content, actions, classes, customSxStyles } = props;
  const sxStyles = useSxStyles();
  const { t } = useTranslation();

  const sxCustom: CustomSxStyles = {
    root: mergeSxProps(sxStyles.root, customSxStyles?.root),
    header: mergeSxProps(sxStyles.header, customSxStyles?.header),
    content: mergeSxProps(sxStyles.content, customSxStyles?.content),
    actions: mergeSxProps(sxStyles.actions, customSxStyles?.actions),
  };

  return (
    <Card sx={sxCustom.root} data-cy={props.dataCy}>
      <CardHeader
        // Use some static class to allow some style manipulations
        // from exterior without too many complications
        className="card-header"
        sx={{
          ...sxCustom.header,
          'div[class$="content"]': { ...sxStyles.headerContent },
        }}
        title={
          typeof title === 'string' ? (
            <TooltippedTruncatedText>{title}</TooltippedTruncatedText>
          ) : (
            title
          )
        }
        titleTypographyProps={{
          noWrap: true,
          color: 'primary',
          variant: 'subtitle2',
        }}
        data-cy={'widget-title'}
      />
      <ErrorsHandler
        errorMessage={t('errorMessage')}
        errorMessageSx={sxStyles.errorHandler}
      >
        <CardContent className={classes?.content} sx={sxCustom.content}>
          {content}
        </CardContent>
        {actions && (
          <CardActions className={classes?.actions} sx={sxCustom.actions}>
            {actions}
          </CardActions>
        )}
      </ErrorsHandler>
    </Card>
  );
};
